import { useCallback, useContext, useMemo, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import MetricsContext from 'contexts/MetricsContext';
import metricTypeCheckers from 'types/metricTypeCheckers';

import useFieldLabel from './useFieldLabel';

import {
  Breakdown,
  BreakdownSuggestion,
  GenerateReportRequest,
  SuggestBreakdownsResponse,
} from './types';

const SUGGEST_BREAKDOWNS_PATH = '/v1/content/suggestReportBreakdowns';

const ENTITY_FIELD_PRIORITY = [
  'orderNo',
  'driver',
  'truck',
  'customer',
  'salesRep',
  'billTo',
  'driverManager',
  'fleetManager',
];

const useFormState = ({
  initialConfig,
  initialName,
}: {
  initialConfig?: ReportGenerator.MetricReportConfig;
  initialName?: string;
} = {}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { allMetrics } = useContext(MetricsContext);
  const getFieldLabel = useFieldLabel();

  const [name, setName] = useState<string>(initialName ?? '');
  const [metricId, setMetricId] = useState<string | undefined>(
    initialConfig?.metricId,
  );
  const [selectedMetric, setSelectedMetric] = useState<
    Metrics.Metric | undefined
  >();
  const [dimensions, setDimensions] = useState<string[]>(
    initialConfig ? initialConfig.breakdowns.map((b) => b.dimension) : [],
  );

  const [supportingMetricIds, setSupportingMetricIds] = useState<string[]>(
    initialConfig?.supportingMetricIds ?? [],
  );

  const [isSuggesting, setIsSuggesting] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<
    BreakdownSuggestion[] | undefined
  >(
    initialConfig
      ? initialConfig.breakdowns.map((b) => ({ ...b }) as BreakdownSuggestion)
      : undefined,
  );
  const [suggestError, setSuggestError] = useState<string | undefined>();

  const [breakdowns, setBreakdowns] = useState<Breakdown[]>(
    initialConfig
      ? initialConfig.breakdowns.map((b) => ({
          dimension: b.dimension,
          gadgetType: b.gadgetType as Breakdown['gadgetType'],
          dateInterval: b.dateInterval as Breakdown['dateInterval'],
          limit: b.limit as Breakdown['limit'],
          fieldType: b.fieldType as FleetOps.FieldType,
          width: b.width,
        }))
      : [],
  );
  const [resolvedDataset, setResolvedDataset] = useState<string | undefined>(
    initialConfig?.dataset,
  );
  const [resolvedMetricField, setResolvedMetricField] = useState<
    string | undefined
  >(initialConfig?.metricField);

  const initialDetailFields = initialConfig?.detail?.fields ?? [];
  const [detailFields, setDetailFields] =
    useState<string[]>(initialDetailFields);
  const [detailSortField, setDetailSortField] = useState<string | undefined>(
    initialConfig?.detail?.sortBy.field ?? initialDetailFields[0],
  );
  const [detailSortDirection, setDetailSortDirection] = useState<
    'asc' | 'desc'
  >(initialConfig?.detail?.sortBy.direction ?? 'desc');

  const isCompound = useMemo(() => {
    if (!metricId) return false;
    const metric = metricOptionsLookup[metricId];
    return !!metric && metricTypeCheckers.isCompoundMetric(metric);
  }, [metricId, metricOptionsLookup]);

  const metricDataset = useMemo((): string | undefined => {
    if (!selectedMetric) {
      return initialConfig?.dataset;
    }
    if (metricTypeCheckers.isNormalMetric(selectedMetric)) {
      return selectedMetric.dataType;
    }
    if (metricTypeCheckers.isCompoundMetric(selectedMetric)) {
      const firstId = selectedMetric.metricIds?.[0];
      if (!firstId) {
        return undefined;
      }
      const firstNormal = allMetrics.find((m) => m.id === firstId);
      return firstNormal?.dataType;
    }
    return undefined;
  }, [allMetrics, initialConfig, selectedMetric]);

  const dimensionDefinition = useMemo(() => {
    if (!metricDataset) {
      return undefined;
    }
    return datasets.find((d) => d.type === metricDataset);
  }, [datasets, metricDataset]);

  const dimensionFieldOptions = useMemo((): DropdownOption[] => {
    if (!dimensionDefinition || !metricDataset) {
      return [];
    }
    return dimensionDefinition.fields
      .filter(
        (f) => f.type === 'text' || f.type === 'date' || f.type === 'dateText',
      )
      .map((f) => ({
        key: f.field,
        label: getFieldLabel({ field: f.field, dataType: metricDataset }),
        isSelected: dimensions.includes(f.field),
        onSelected: () => {
          setDimensions((prev) => {
            if (prev.includes(f.field)) {
              return prev.filter((d) => d !== f.field);
            }
            return [...prev, f.field];
          });
        },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensionDefinition, dimensions, getFieldLabel, metricDataset]);

  const sameDatasetMetricIds = useMemo((): string[] => {
    if (!metricDataset) return [];
    return allMetrics
      .filter(
        (m) =>
          metricTypeCheckers.isNormalMetric(m) && m.dataType === metricDataset,
      )
      .map((m) => m.id);
  }, [allMetrics, metricDataset]);

  const supportingMetrics = useMemo((): Metrics.Metric[] => {
    const result: Metrics.Metric[] = [];
    for (const id of supportingMetricIds) {
      const metric = allMetrics.find((m) => m.id === id);
      if (metric) result.push(metric);
    }
    return result;
  }, [allMetrics, supportingMetricIds]);

  const onMetricSelected = useCallback((metric?: Metrics.Metric) => {
    setSelectedMetric(metric);
    setMetricId(metric?.id);
    setDimensions([]);
    setSuggestions(undefined);
    setSuggestError(undefined);
    setBreakdowns([]);
    setResolvedDataset(undefined);
    setResolvedMetricField(undefined);
    setDetailFields([]);
    setDetailSortField(undefined);
    setSupportingMetricIds([]);
  }, []);

  const onSupportingMetricAdded = useCallback((id: string) => {
    setSupportingMetricIds((prev) =>
      prev.includes(id) ? prev : [...prev, id],
    );
  }, []);

  const onSupportingMetricRemoved = useCallback((id: string) => {
    setSupportingMetricIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const onSuggestClicked = useCallback(async () => {
    if (!metricId || dimensions.length === 0) {
      return;
    }

    setIsSuggesting(true);
    setSuggestError(undefined);

    try {
      const result = await api.post<SuggestBreakdownsResponse>(
        SUGGEST_BREAKDOWNS_PATH,
        {
          accountId: selectedAccountId,
          metricId,
          isCompound,
          dimensions,
        },
      );

      if (!result.ok || !result.data) {
        throw new Error(result.problem ?? 'Request failed');
      }

      const data = result.data;
      setSuggestions(data.suggestions);
      setResolvedDataset(data.dataset);
      setResolvedMetricField(data.metricField);

      const hasSupportingMetrics = supportingMetricIds.length > 0;
      setBreakdowns(
        data.suggestions.map((s: BreakdownSuggestion) => ({
          dimension: s.dimension,
          gadgetType:
            hasSupportingMetrics && s.gadgetType !== 'timeSeries'
              ? 'matrix'
              : s.gadgetType,
          dateInterval: s.dateInterval,
          limit: s.limit,
          fieldType: s.fieldType,
        })),
      );

      // Pre-populate detail fields: priority entity field + dimensions + metric field
      const datasetDef = datasets.find((d) => d.type === data.dataset);
      const availableFields = datasetDef
        ? datasetDef.fields.map((f) => f.field)
        : [];

      const entityField = ENTITY_FIELD_PRIORITY.find((f) =>
        availableFields.includes(f),
      );

      const prePopulated = [
        ...(entityField ? [entityField] : []),
        ...dimensions.filter((d) => d !== entityField),
        ...(data.metricField && !dimensions.includes(data.metricField)
          ? [data.metricField]
          : []),
      ].filter((f, i, arr) => arr.indexOf(f) === i);

      setDetailFields(prePopulated);
      setDetailSortField(prePopulated[0]);
    } catch (err) {
      console.error('suggestBreakdowns failed:', err);
      setSuggestError('Failed to get suggestions. Please try again.');
    } finally {
      setIsSuggesting(false);
    }
  }, [
    api,
    datasets,
    dimensions,
    isCompound,
    metricId,
    selectedAccountId,
    supportingMetricIds,
  ]);

  const onBreakdownGadgetTypeChanged = useCallback(
    (index: number, gadgetType: Breakdown['gadgetType']) => {
      setBreakdowns((prev) =>
        prev.map((b, i) => (i === index ? { ...b, gadgetType } : b)),
      );
    },
    [],
  );

  const onBreakdownLimitChanged = useCallback(
    (index: number, limit: Breakdown['limit']) => {
      setBreakdowns((prev) =>
        prev.map((b, i) => (i === index ? { ...b, limit } : b)),
      );
    },
    [],
  );

  const detailFieldOptions = useMemo((): DropdownOption[] => {
    if (!resolvedDataset) {
      return [];
    }
    const datasetDef = datasets.find((d) => d.type === resolvedDataset);
    if (!datasetDef) {
      return [];
    }
    return datasetDef.fields.map((f) => ({
      key: f.field,
      label: f.alias ?? f.field,
      isSelected: detailFields.includes(f.field),
      onSelected: () => {
        setDetailFields((prev) => {
          if (prev.includes(f.field)) {
            const next = prev.filter((x) => x !== f.field);
            if (detailSortField === f.field) {
              setDetailSortField(next[0]);
            }
            return next;
          }
          return [...prev, f.field];
        });
      },
    }));
  }, [detailFields, detailSortField, datasets, resolvedDataset]);

  const detailSortOptions = useMemo((): DropdownOption[] => {
    const datasetDef = datasets.find((d) => d.type === resolvedDataset);
    return detailFields.map((f) => {
      const fieldDef = datasetDef?.fields.find((fd) => fd.field === f);
      return {
        key: f,
        label: fieldDef?.alias ?? f,
        isSelected: detailSortField === f,
        onSelected: () => setDetailSortField(f),
      };
    });
  }, [datasets, detailFields, detailSortField, resolvedDataset]);

  const detailSortDirectionOptions = useMemo(
    (): DropdownOption[] => [
      {
        key: 'desc',
        label: 'Descending',
        isSelected: detailSortDirection === 'desc',
        onSelected: () => setDetailSortDirection('desc'),
      },
      {
        key: 'asc',
        label: 'Ascending',
        isSelected: detailSortDirection === 'asc',
        onSelected: () => setDetailSortDirection('asc'),
      },
    ],
    [detailSortDirection],
  );

  const buildGenerateRequest = useCallback(():
    | GenerateReportRequest
    | undefined => {
    if (!metricId || breakdowns.length === 0 || name === '') {
      return undefined;
    }

    const detail =
      detailFields.length > 0 && detailSortField
        ? {
            fields: detailFields,
            sortBy: { field: detailSortField, direction: detailSortDirection },
          }
        : undefined;

    return {
      accountId: selectedAccountId,
      name,
      metricId,
      isCompound,
      breakdowns,
      detail,
      ...(supportingMetricIds.length > 0 ? { supportingMetricIds } : {}),
    };
  }, [
    breakdowns,
    detailFields,
    detailSortDirection,
    detailSortField,
    isCompound,
    metricId,
    name,
    selectedAccountId,
    supportingMetricIds,
  ]);

  const canSuggest = !!metricId && dimensions.length > 0 && !isSuggesting;

  const canGenerate =
    name !== '' && !!metricId && breakdowns.length > 0 && !isSuggesting;

  return {
    name,
    setName,
    metricId,
    selectedMetric,
    onMetricSelected,
    dimensions,
    dimensionFieldOptions,
    isSuggesting,
    suggestions,
    suggestError,
    onSuggestClicked,
    canSuggest,
    breakdowns,
    onBreakdownGadgetTypeChanged,
    onBreakdownLimitChanged,
    supportingMetricIds,
    supportingMetrics,
    sameDatasetMetricIds,
    onSupportingMetricAdded,
    onSupportingMetricRemoved,
    detailFields,
    detailFieldOptions,
    detailSortField,
    detailSortOptions,
    detailSortDirection,
    detailSortDirectionOptions,
    canGenerate,
    resolvedDataset,
    resolvedMetricField,
    isCompound,
    buildGenerateRequest,
  };
};

export default useFormState;
